import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  LeftOutlined,
  ArrowRightOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import StdSeedRate from "../StdSeedRate";
import { ReactComponent as EditIcon } from "../../../../assets/images/product-card-edit.svg";
import PlotSummary from "../PlotSummary";
import ResetBtn from "../ResetBtn";
import WarningConfirmationPopover from "../../../../shared/components/WarningConfirmationPopover";
import AutoProdAssignment from "../AutoProdAssignment";
import { ErrorAutoScreen } from "../ErrorAutoScreen";
import StripeAutoAssignment from "../StripeAutoAssignment";
import StripeManualAssignmentTable from "../StripeManualAssignmentTable";
import {
  type BeforeBackSeeds,
  type BeforeLeaveSeeds,
  type ResetSeeds,
} from "../SeedsTab";
import { usePlotSelection } from "../../context/PlotSelectionContext";
import { useTrialsRouter } from "../../../../shared/custom-hooks/useTrialsRouter";
import { useNewTrialState } from "../../legacy";
import {
  type SeedProduct,
  type SeedStripe,
  type StripeAssignment,
  type StripeAssignments,
} from "../../utils/seedFlow";
import useStripeSeedData, {
  FIELD_STANDARD_PRODUCT_ID,
} from "../../hooks/useStripeSeedData";
import useStripSeedAssignments, {
  StripSeedConfirmationError,
  stripSeedTrialKey,
} from "../../hooks/useStripSeedAssignments";
import {
  buildStripSeedPayload,
  hydrateStripSeedAssignments,
  hydrateStripSeedSuggestions,
  validateStripSeedTrial,
  type StripSeedTrial,
  type StripStandardProduct,
} from "../../services/stripSeedAssignments";
import {
  fetchSeedTrial,
  resetAllSeedAssignments,
  resetSeedAssignments,
  seedApiError,
} from "../../services/seedAssignments";
import { triggerSeedAgOperation } from "../../services/seedAgOperation";
import {
  getNonEmptySeedColor,
  getStripSeedDisplayColors,
} from "../../utils/stripSeedPresentation";
import { getSeedProductFamilyColor } from "../../utils/seedProductColors";
import useSeedTranslations from "../../hooks/useSeedTranslations";
import seedRateDisplayUnit from "../../utils/seedRateDisplayUnit";
import "./style.less";

interface StripeSeedsTabProps {
  stripes: SeedStripe[];
  onBack: () => void;
  onContinue: () => void;
  nextLabel?: string;
  onValidationChange: (valid: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
  onManualPreviewChange?: (ids: string[]) => void;
  onManualSelectionActiveChange?: (active: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
  onRegisterBeforeLeave?: (handler: BeforeLeaveSeeds | null) => void;
  onRegisterBeforeBack?: (handler: BeforeBackSeeds | null) => void;
  onRegisterReset?: (handler: ResetSeeds | null) => void;
  onResetAvailabilityChange?: (available: boolean) => void;
  screenshotMock?: boolean;
  active?: boolean;
}

const defaultProductRate = (
  product: SeedProduct | undefined
): number | null => {
  if (product === undefined) return null;

  return product.densityOptions?.[0] ?? (product.min > 0 ? product.min : null);
};

const StripeSeedsTabContent = ({
  stripes,
  onBack,
  onContinue,
  nextLabel,
  onValidationChange,
  onDirtyChange,
  onManualPreviewChange,
  onManualSelectionActiveChange,
  onSavingChange,
  onRegisterBeforeLeave,
  onRegisterBeforeBack,
  onRegisterReset,
  onResetAvailabilityChange,
  active = true,
}: StripeSeedsTabProps): JSX.Element => {
  const text = useSeedTranslations();
  const queryClient = useQueryClient();
  const api = useStripSeedAssignments();
  const data = useStripeSeedData(stripes, api.trial, api.isLoading);
  const displayUnit = seedRateDisplayUnit(data.unit);
  const plotSelection = usePlotSelection();

  const [standardProduct, setStandardProduct] = useState<string>();
  const [standardRate, setStandardRate] = useState<number | null>(null);
  const [assignments, setAssignments] = useState<StripeAssignments>({});
  const [persisted, setPersisted] = useState<StripeAssignments>({});
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, number | null>>({});
  const [showAutoAssignment, setShowAutoAssignment] = useState(false);
  const [autoDraft, setAutoDraft] = useState(false);
  const [standardStripeIds, setStandardStripeIds] = useState<string[]>([]);
  const [standardAssignmentSnapshots, setStandardAssignmentSnapshots] =
    useState<Record<string, StripeAssignment>>({});
  const [overwriteConfirmationOpen, setOverwriteConfirmationOpen] =
    useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [autoAssignmentFailed, setAutoAssignmentFailed] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [reloadRequired, setReloadRequired] = useState(false);
  const [retainedIds, setRetainedIds] = useState<string[]>([]);

  const initialized = useRef(false);
  const operationLock = useRef(false);
  const mounted = useRef(true);
  const continueRequested = useRef(false);
  const latestSession = useRef(plotSelection.session);
  latestSession.current = plotSelection.session;

  const busy = api.saving || loadingSuggestions || resetting || continuing;

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!data.ready || initialized.current) return;

    initialized.current = true;
    setStandardProduct(data.initialStandardProduct);
    setStandardRate(data.initialStandardRate);
    setAssignments(data.initialAssignments);
    setPersisted(data.initialAssignments);
    const standardIds = Object.entries(data.initialAssignments)
      .filter(
        ([, assignment]) => assignment.productId === FIELD_STANDARD_PRODUCT_ID
      )
      .map(([id]) => id);
    setStandardStripeIds(standardIds);
    setStandardAssignmentSnapshots(
      Object.fromEntries(
        standardIds.map((id) => [id, { ...data.initialAssignments[id] }])
      )
    );
  }, [
    data.ready,
    data.initialStandardProduct,
    data.initialStandardRate,
    data.initialAssignments,
  ]);

  const loadError =
    data.hydrationError ??
    (api.loadError !== null && api.loadError !== undefined
      ? seedApiError(api.loadError).message
      : null);

  useEffect(() => {
    if (loadError !== null) {
      toast.error(loadError, {
        toastId: `strip-seeds-load-${api.trialId}`,
      });
    }
  }, [loadError, api.trialId]);

  useEffect(() => {
    onSavingChange?.(busy);
  }, [busy, onSavingChange]);

  useEffect(
    () => () => {
      onSavingChange?.(false);
    },
    [onSavingChange]
  );

  const selectedStandardVariety = data.standardVarieties.find(
    (product) => product.id === standardProduct
  );

  // Do not silently change saved standard-strip ownership when the
  // surrounding field's standard selector changes.
  const standardAssignmentProduct =
    data.standardAssignmentProduct ?? selectedStandardVariety;

  const availableProducts = useMemo<SeedProduct[]>(
    () =>
      standardAssignmentProduct === undefined
        ? data.products
        : [
            ...data.products,
            {
              ...standardAssignmentProduct,
              id: FIELD_STANDARD_PRODUCT_ID,
            },
          ],
    [data.products, standardAssignmentProduct]
  );

  const actualStandard: StripStandardProduct | undefined =
    standardAssignmentProduct !== undefined &&
    standardAssignmentProduct.id.trim() !== ""
      ? {
          id: standardAssignmentProduct.id,
          name: standardAssignmentProduct.name,
        }
      : undefined;

  const editingProduct = availableProducts.find(
    (product) => product.id === editingProductId
  );

  const isManualSelectionActive = editingProductId !== null;
  const isEditingStandard = editingProductId === FIELD_STANDARD_PRODUCT_ID;

  const activeStripeSession =
    plotSelection.session?.tab === "seeds" &&
    plotSelection.session.targetMode === "stripe" &&
    plotSelection.session.ownerId === editingProductId
      ? plotSelection.session
      : null;

  const selectedIds = activeStripeSession?.selectedIds ?? retainedIds;

  useEffect(() => {
    if (activeStripeSession !== null) {
      setRetainedIds(activeStripeSession.selectedIds);
    }
  }, [activeStripeSession]);

  const getRateError = (
    rate: number | null | undefined,
    product?: SeedProduct
  ): string | undefined => {
    if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0) {
      return text("positiveRateError");
    }

    if (product !== undefined && rate < product.min) {
      return text("minRateError", { min: product.min });
    }

    if (product !== undefined && rate > product.max) {
      return text("maxRateError", { max: product.max });
    }

    const options = product?.densityOptions ?? [];

    if (options.length > 0 && !options.includes(rate)) {
      return text("allowedRates", { rates: options.join(", ") });
    }

    return undefined;
  };

  const rangeLabel = (product: SeedProduct): string => {
    const options = product.densityOptions ?? [];

    if (options.length > 0) {
      return `${text("rates", { rates: options.join(", ") })} ${displayUnit}`;
    }

    if (Number.isFinite(product.max)) {
      return `${text("range", {
        min: product.min,
        max: product.max,
      })} ${displayUnit}`;
    }

    if (product.min > 0) {
      return `${text("minimumRate", { min: product.min })} ${displayUnit}`;
    }

    return text("noRange");
  };

  useEffect(() => {
    if (editingProduct === undefined) return;

    setDraft((previous) => {
      const next = { ...previous };
      let changed = false;

      selectedIds.forEach((id) => {
        if (!Object.prototype.hasOwnProperty.call(next, id)) {
          const existing = assignments[id];

          next[id] =
            existing?.productId === editingProduct.id
              ? existing.rate
              : isEditingStandard
              ? standardRate
              : defaultProductRate(editingProduct);

          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [
    editingProduct,
    selectedIds,
    assignments,
    isEditingStandard,
    standardRate,
  ]);

  useEffect(() => {
    if (
      !active ||
      editingProduct === undefined ||
      activeStripeSession !== null ||
      busy ||
      reloadRequired
    ) {
      return;
    }

    plotSelection.startSession({
      tab: "seeds",
      ownerId: editingProduct.id,
      targetMode: "stripe",
      targetIds: stripes.map((stripe) => stripe.id),
      selectedIds: retainedIds,
      disabledIds: stripes
        .filter(({ id }) => assignments[id]?.legacy === true)
        .map((stripe) => stripe.id),
      color: editingProduct.color,
    });
  }, [
    active,
    editingProduct,
    activeStripeSession,
    busy,
    reloadRequired,
    retainedIds,
    assignments,
    stripes,
    plotSelection.startSession,
  ]);

  const standardDirty =
    (standardProduct ?? "") !== (data.initialStandardProduct ?? "") ||
    standardRate !== data.initialStandardRate;

  const standardError =
    standardProduct !== undefined ? getRateError(standardRate) : undefined;

  const canUseStandardRate =
    selectedStandardVariety !== undefined &&
    getRateError(standardRate) === undefined;

  const effectiveAssignments = useMemo<StripeAssignments>(() => {
    const result: StripeAssignments = {};

    stripes.forEach(({ id }) => {
      const assignment = assignments[id];

      if (assignment === undefined) return;

      result[id] = assignment;
    });

    return result;
  }, [stripes, assignments]);

  const assignmentErrors: Record<string, string | undefined> = {};

  stripes.forEach(({ id }) => {
    const assignment = effectiveAssignments[id];
    const product = availableProducts.find(
      (item) => item.id === assignment?.productId
    );

    assignmentErrors[id] =
      assignment === undefined || product === undefined
        ? text("selectProductError")
        : getRateError(
            assignment.rate,
            assignment.productId === FIELD_STANDARD_PRODUCT_ID
              ? undefined
              : product
          );
  });

  const validAssignmentCount = stripes.filter(
    ({ id }) => assignmentErrors[id] === undefined
  ).length;

  const usedProductCount = new Set(
    Object.values(effectiveAssignments).map((item) => item.productId)
  ).size;

  const isComplete =
    data.ready &&
    initialized.current &&
    loadError === null &&
    !busy &&
    !reloadRequired &&
    stripes.length > 0 &&
    validAssignmentCount === stripes.length &&
    standardError === undefined &&
    !isManualSelectionActive;

  // Saved assignments are grouped by product, not by display strip order.
  const assignmentsDirty =
    Object.keys(effectiveAssignments).length !==
      Object.keys(persisted).length ||
    stripes.some(({ id }) => {
      const current = effectiveAssignments[id];
      const saved = persisted[id];
      return (
        current?.productId !== saved?.productId ||
        current?.rate !== saved?.rate ||
        current?.color !== saved?.color ||
        current?.legacy !== saved?.legacy
      );
    });

  const manualAssignmentDraftDirty =
    isManualSelectionActive &&
    editingProduct !== undefined &&
    stripes.some(({ id }) => {
      const current = assignments[id];
      const selected = selectedIds.includes(id);

      return (
        selected !== (current?.productId === editingProduct.id) ||
        (selected &&
          (current?.productId !== editingProduct.id ||
            current.rate !==
              (isEditingStandard ? standardRate : draft[id] ?? null)))
      );
    });

  const isDirty =
    standardDirty || assignmentsDirty || isManualSelectionActive || autoDraft;

  const hasExistingProductAssignments =
    editingProductId !== null &&
    stripes.some(({ id }) => assignments[id]?.productId === editingProductId);

  const hasPersistedProductAssignments =
    editingProductId !== null &&
    Object.values(persisted).some(
      (assignment) => assignment.productId === editingProductId
    );

  const canSave =
    editingProduct !== undefined &&
    !busy &&
    !reloadRequired &&
    plotSelection.pendingConflict === null &&
    (selectedIds.length > 0 || hasExistingProductAssignments) &&
    selectedIds.every((id) => {
      const owner = assignments[id]?.productId;
      const ownerAllowed =
        owner === undefined ||
        owner === editingProductId ||
        activeStripeSession?.reassignedIds.includes(id) === true;

      return (
        ownerAllowed &&
        getRateError(
          isEditingStandard ? standardRate : draft[id],
          isEditingStandard ? undefined : editingProduct
        ) === undefined
      );
    });

  const summaryAssignments = useMemo<StripeAssignments>(() => {
    const result: StripeAssignments = {};
    const selected = new Set(selectedIds);

    stripes.forEach(({ id }) => {
      const saved = effectiveAssignments[id];

      if (
        saved !== undefined &&
        (!isManualSelectionActive || saved.productId !== editingProductId)
      ) {
        result[id] = saved;
      }

      if (editingProduct !== undefined && selected.has(id)) {
        result[id] = {
          productId: editingProduct.id,
          rate: isEditingStandard ? standardRate : draft[id] ?? null,
          color:
            saved?.productId === editingProduct.id
              ? getNonEmptySeedColor(saved.color, editingProduct.color) ?? ""
              : editingProduct.color,
          legacy: false,
        };
      }
    });

    return result;
  }, [
    stripes,
    effectiveAssignments,
    isManualSelectionActive,
    editingProductId,
    editingProduct,
    selectedIds,
    isEditingStandard,
    standardRate,
    draft,
  ]);

  const stripeColors = useMemo(
    () => getStripSeedDisplayColors(summaryAssignments, availableProducts),
    [summaryAssignments, availableProducts]
  );

  const summarySelectedCount = Object.keys(summaryAssignments).length;

  const summaryProductCount = new Set(
    Object.values(summaryAssignments).map((item) => item.productId)
  ).size;

  const manualRateErrors: Record<string, string | undefined> = {};
  const manualDisplayRates: Record<string, number | null> = {};

  selectedIds.forEach((id) => {
    const rate = isEditingStandard ? standardRate : draft[id] ?? null;
    manualDisplayRates[id] = rate;
    manualRateErrors[id] = getRateError(
      rate,
      isEditingStandard ? undefined : editingProduct
    );
  });

  useEffect(() => {
    onValidationChange(isComplete);
  }, [isComplete, onValidationChange]);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    onManualSelectionActiveChange?.(active && isManualSelectionActive);
  }, [active, isManualSelectionActive, onManualSelectionActiveChange]);

  useEffect(() => {
    onManualPreviewChange?.(
      active
        ? isManualSelectionActive
          ? selectedIds
          : Object.keys(effectiveAssignments)
        : []
    );
  }, [
    active,
    isManualSelectionActive,
    selectedIds,
    effectiveAssignments,
    onManualPreviewChange,
  ]);

  useEffect(() => {
    // This context value is a map-facing projection, not persisted state.
    // Cancel restores the projection from the unchanged assignments.
    plotSelection.setAssignedSeedStripes(
      stripes.flatMap(({ id }) => {
        const assignment = summaryAssignments[id];

        if (assignment === undefined) return [];

        const product = availableProducts.find(
          (item) => item.id === assignment.productId
        );

        return [
          {
            cellId: id,
            ownerId: assignment.productId,
            ownerLabel: product?.name ?? assignment.productId,
            color:
              getNonEmptySeedColor(
                stripeColors[id],
                assignment.color,
                product?.color
              ) ?? "",
            isStandard: assignment.productId === FIELD_STANDARD_PRODUCT_ID,
          },
        ];
      })
    );
  }, [
    stripes,
    summaryAssignments,
    stripeColors,
    availableProducts,
    plotSelection.setAssignedSeedStripes,
  ]);

  const closeManualSelection = useCallback((): void => {
    if (operationLock.current) return;

    if (editingProductId !== null) {
      plotSelection.endSession({
        tab: "seeds",
        ownerId: editingProductId,
        targetMode: "stripe",
      });
    }

    setEditingProductId(null);
    setDraft({});
    setRetainedIds([]);
  }, [editingProductId, plotSelection.endSession]);

  useEffect(
    () => () => {
      const session = latestSession.current;

      if (session?.tab === "seeds" && session.targetMode === "stripe") {
        plotSelection.endSession({
          tab: "seeds",
          ownerId: session.ownerId,
          targetMode: "stripe",
        });
      }

      onManualPreviewChange?.([]);
      onManualSelectionActiveChange?.(false);
    },
    [
      plotSelection.endSession,
      onManualPreviewChange,
      onManualSelectionActiveChange,
    ]
  );

  const reportFailure = (error: unknown): void => {
    if (!mounted.current) return;

    const failure = seedApiError(error);
    const mustReload =
      failure.reloadRequired || error instanceof StripSeedConfirmationError;

    setReloadRequired(mustReload);
    toast.error(
      mustReload
        ? `${failure.message} Your draft is retained. Reload this page before retrying.`
        : failure.message
    );
  };

  const reconcile = (trial: StripSeedTrial): StripeAssignments => {
    const restored = hydrateStripSeedAssignments(trial, stripes, data.unit);

    if (mounted.current) {
      setPersisted(restored);
      setAssignments(restored);
      const standardIds = Object.entries(restored)
        .filter(
          ([, assignment]) => assignment.productId === FIELD_STANDARD_PRODUCT_ID
        )
        .map(([id]) => id);
      setStandardStripeIds(standardIds);
      setStandardAssignmentSnapshots(
        Object.fromEntries(standardIds.map((id) => [id, { ...restored[id] }]))
      );
      setAutoDraft(false);
    }

    return restored;
  };

  const openProduct = (productId: string): void => {
    if (
      !active ||
      !data.ready ||
      busy ||
      reloadRequired ||
      isManualSelectionActive ||
      autoDraft
    ) {
      return;
    }

    const owner = availableProducts.find((item) => item.id === productId);
    if (owner === undefined) return;

    const owned = stripes.filter(
      ({ id }) => assignments[id]?.productId === productId
    );

    if (owned.some(({ id }) => assignments[id]?.legacy === true)) {
      toast.error(
        "Legacy assignments without product_id cannot be edited safely."
      );
      return;
    }

    const rates = Object.fromEntries(
      owned.map(({ id }) => [id, assignments[id].rate])
    );

    setDraft(rates);
    setRetainedIds(Object.keys(rates));
    setEditingProductId(productId);

    plotSelection.startSession({
      tab: "seeds",
      ownerId: productId,
      targetMode: "stripe",
      targetIds: stripes.map((stripe) => stripe.id),
      selectedIds: Object.keys(rates),
      disabledIds: stripes
        .filter(({ id }) => assignments[id]?.legacy === true)
        .map((stripe) => stripe.id),
      color: owner.color,
    });
  };

  const resetManualSelection = async (): Promise<void> => {
    const trial = api.trial;
    const productId = editingProductId;

    if (
      !active ||
      !data.ready ||
      trial === undefined ||
      productId === null ||
      busy ||
      reloadRequired ||
      operationLock.current
    ) {
      return;
    }

    const ownedAssignments = Object.values(persisted).filter(
      (assignment) => assignment.productId === productId
    );

    const savedProducts =
      trial.plot_property_assignments?.seed_product_assignments ?? [];

    const isStandardProduct = productId === FIELD_STANDARD_PRODUCT_ID;
    const savedProductId = isStandardProduct
      ? savedProducts.find((product) => product.is_standard)?.product_id
      : productId;

    const matchingProducts = savedProducts.filter(
      (product) =>
        product.product_id === savedProductId &&
        product.is_standard === isStandardProduct
    );

    if (
      ownedAssignments.some((assignment) => assignment.legacy === true) ||
      matchingProducts.some((product) => product.product_id === null)
    ) {
      toast.error("Legacy assignments cannot be reset in this editor.");
      return;
    }

    const hasSavedAssignments = matchingProducts.some(
      (product) => product.assignments.length > 0
    );

    if (ownedAssignments.length === 0 && !hasSavedAssignments) {
      plotSelection.setSelectedIds([]);
      setRetainedIds([]);
      setDraft({});
      return;
    }

    if (!savedProductId) {
      toast.error("The saved seed product could not be identified for reset.");
      return;
    }

    operationLock.current = true;
    setResetting(true);

    const queryKey = stripSeedTrialKey(api.trialId, api.scope);

    let resetSucceeded = false;
    let confirmedReset: StripSeedTrial | null = null;

    const confirmReset = (updatedTrial: StripSeedTrial): void => {
      const restored = hydrateStripSeedAssignments(
        updatedTrial,
        stripes,
        data.unit
      );

      if (
        Object.values(restored).some(
          (assignment) => assignment.productId === productId
        )
      ) {
        throw new Error(
          "The reset response still contains assignments for this product."
        );
      }
    };

    try {
      await queryClient.cancelQueries({
        queryKey,
        exact: true,
      });

      const response = isStandardProduct
        ? await resetSeedAssignments(trial, savedProductId, api.scope, true)
        : await resetSeedAssignments(trial, savedProductId, api.scope);

      resetSucceeded = true;

      if (!Array.isArray(response?.seed_product_assignments)) {
        throw new Error(
          "The reset API returned an invalid seed-assignment response."
        );
      }

      const updatedTrial = validateStripSeedTrial({
        ...trial,
        plot_property_assignments: {
          ...trial.plot_property_assignments,
          ...response,
        },
      });

      confirmReset(updatedTrial);
      confirmedReset = updatedTrial;

      reconcile(updatedTrial);
      queryClient.setQueryData(queryKey, updatedTrial);

      if (mounted.current) {
        plotSelection.endSession({
          tab: "seeds",
          ownerId: productId,
          targetMode: "stripe",
        });

        setEditingProductId(null);
        setDraft({});
        setRetainedIds([]);
        onManualPreviewChange?.([]);
      }

      const freshTrial = validateStripSeedTrial(
        await fetchSeedTrial(api.trialId, api.scope)
      );

      confirmReset(freshTrial);

      queryClient.setQueryData(queryKey, freshTrial);
      reconcile(freshTrial);

      await queryClient.invalidateQueries(
        {
          queryKey: ["trial", api.trialId],
        },
        {
          throwOnError: true,
        }
      );

      await queryClient.invalidateQueries(
        {
          queryKey: ["trialInsights"],
        },
        {
          throwOnError: true,
        }
      );
    } catch (error) {
      if (resetSucceeded) {
        if (confirmedReset !== null) {
          queryClient.setQueryData(queryKey, confirmedReset);
          reconcile(confirmedReset);
        }

        void queryClient.invalidateQueries({
          queryKey: ["trial", api.trialId],
          refetchType: "none",
        });

        void queryClient.invalidateQueries({
          queryKey: ["trialInsights"],
          refetchType: "none",
        });

        if (mounted.current) {
          setReloadRequired(true);
          toast.error(
            "The reset request succeeded, but the latest seed assignments " +
              "could not be confirmed. Reload before continuing."
          );
        }
      } else {
        reportFailure(error);
      }
    } finally {
      operationLock.current = false;

      if (mounted.current) {
        setResetting(false);
      }
    }
  };

  const topResetProductIds = Array.from(
    new Set(
      Object.values(persisted)
        .filter((assignment) => !assignment.legacy)
        .map((assignment) => assignment.productId)
    )
  );
  const topResetAvailable =
    active &&
    data.ready &&
    topResetProductIds.length > 0 &&
    !isManualSelectionActive &&
    !assignmentsDirty &&
    !standardDirty &&
    !autoDraft &&
    !showAutoAssignment &&
    !busy &&
    !reloadRequired;

  const resetAllSavedAssignments = async (): Promise<boolean> => {
    const trial = api.trial;

    if (
      trial === undefined ||
      !topResetAvailable ||
      operationLock.current ||
      busy ||
      reloadRequired
    ) {
      return false;
    }

    operationLock.current = true;
    setResetting(true);

    const queryKey = stripSeedTrialKey(api.trialId, api.scope);
    let resetSucceeded = false;
    let confirmedReset: StripSeedTrial | null = null;

    const confirmReset = (updatedTrial: StripSeedTrial): void => {
      const restored = hydrateStripSeedAssignments(
        updatedTrial,
        stripes,
        data.unit
      );

      if (
        Object.values(restored).some((assignment) =>
          topResetProductIds.includes(assignment.productId)
        )
      ) {
        throw new Error(
          "The reset response still contains applicable seed assignments."
        );
      }
    };

    try {
      await queryClient.cancelQueries({ queryKey, exact: true });

      const response = await resetAllSeedAssignments(
        trial,
        topResetProductIds.map((productId) => {
          if (productId !== FIELD_STANDARD_PRODUCT_ID) return productId;

          const savedId =
            trial.plot_property_assignments?.seed_product_assignments?.find(
              (product) => product.is_standard
            )?.product_id;

          if (!savedId) {
            throw new Error(
              "The saved seed product could not be identified for reset."
            );
          }

          return savedId;
        }),
        api.scope
      );

      if (response === null) {
        return false;
      }

      resetSucceeded = true;

      if (!Array.isArray(response.seed_product_assignments)) {
        throw new Error(
          "The reset API returned an invalid seed-assignment response."
        );
      }

      const updatedTrial = validateStripSeedTrial({
        ...trial,
        plot_property_assignments: {
          ...trial.plot_property_assignments,
          ...response,
        },
      });

      confirmReset(updatedTrial);
      confirmedReset = updatedTrial;
      reconcile(updatedTrial);
      queryClient.setQueryData(queryKey, updatedTrial);

      const freshTrial = validateStripSeedTrial(
        await fetchSeedTrial(api.trialId, api.scope)
      );
      confirmReset(freshTrial);
      queryClient.setQueryData(queryKey, freshTrial);
      reconcile(freshTrial);

      await queryClient.invalidateQueries(
        { queryKey: ["trial", api.trialId] },
        { throwOnError: true }
      );
      await queryClient.invalidateQueries(
        { queryKey: ["trialInsights"] },
        { throwOnError: true }
      );

      onValidationChange(false);
      return true;
    } catch (error) {
      if (resetSucceeded) {
        if (confirmedReset !== null) {
          queryClient.setQueryData(queryKey, confirmedReset);
          reconcile(confirmedReset);
        }

        void queryClient.invalidateQueries({
          queryKey: ["trial", api.trialId],
          refetchType: "none",
        });
        void queryClient.invalidateQueries({
          queryKey: ["trialInsights"],
          refetchType: "none",
        });

        if (mounted.current) {
          setReloadRequired(true);
          toast.error(
            "The reset request succeeded, but the latest seed assignments " +
              "could not be confirmed. Reload before continuing."
          );
        }
      } else {
        reportFailure(error);
      }

      return false;
    } finally {
      operationLock.current = false;
      if (mounted.current) setResetting(false);
    }
  };

  useEffect(() => {
    onRegisterReset?.(resetAllSavedAssignments);

    return () => {
      onRegisterReset?.(null);
    };
  }, [onRegisterReset, resetAllSavedAssignments]);

  useEffect(() => {
    onResetAvailabilityChange?.(topResetAvailable);
  }, [onResetAvailabilityChange, topResetAvailable]);

  const saveProduct = async (): Promise<boolean> => {
    const trial = api.trial;
    const product = editingProduct;

    if (
      trial === undefined ||
      product === undefined ||
      !canSave ||
      operationLock.current
    ) {
      return false;
    }

    operationLock.current = true;
    let patchSucceeded = false;

    try {
      const next: StripeAssignments = {};
      const selected = new Set(selectedIds);

      stripes.forEach(({ id }) => {
        const previous = assignments[id];

        if (selected.has(id)) {
          next[id] = {
            productId: product.id,
            rate: isEditingStandard ? standardRate : draft[id] ?? null,
            color:
              previous?.productId === product.id
                ? previous.color ?? product.color
                : product.color,
            legacy: false,
          };
        } else if (
          previous !== undefined &&
          previous.productId !== product.id
        ) {
          next[id] = previous;
        }
      });

      const payload = buildStripSeedPayload(
        trial,
        stripes,
        persisted,
        next,
        data.products,
        data.unit,
        [product.id],
        actualStandard
      );

      const updated = await api.saveAssignments({ trial, products: payload });
      patchSucceeded = true;
      reconcile(updated);

      if (!mounted.current) return false;

      plotSelection.endSession({
        tab: "seeds",
        ownerId: product.id,
        targetMode: "stripe",
      });

      setEditingProductId(null);
      setDraft({});
      setRetainedIds([]);
      return true;
    } catch (error) {
      reportFailure(patchSucceeded ? new StripSeedConfirmationError() : error);
      return false;
    } finally {
      operationLock.current = false;
    }
  };

  const requestAutoAssignment = async (): Promise<void> => {
    const trial = api.trial;

    if (
      trial === undefined ||
      !data.ready ||
      selectedStandardVariety === undefined ||
      busy ||
      reloadRequired ||
      isManualSelectionActive ||
      operationLock.current
    ) {
      return;
    }

    operationLock.current = true;
    setLoadingSuggestions(true);

    try {
      const response = await api.loadSuggestions();
      const suggestions = hydrateStripSeedSuggestions(
        trial,
        response,
        stripes,
        data.products,
        data.unit,
        actualStandard
      );

      if (!mounted.current) return;

      setAssignments(suggestions);
      const standardIds = Object.entries(suggestions)
        .filter(
          ([, assignment]) => assignment.productId === FIELD_STANDARD_PRODUCT_ID
        )
        .map(([id]) => id);
      setStandardStripeIds(standardIds);
      setStandardAssignmentSnapshots(
        Object.fromEntries(
          standardIds.map((id) => [id, { ...suggestions[id] }])
        )
      );
      setAutoDraft(true);
      setAutoAssignmentFailed(false);
      setShowAutoAssignment(true);
    } catch (error) {
      if (mounted.current) {
        console.error("Failed to auto-assign stripe seed products:", error);
        setAutoAssignmentFailed(true);
      }
    } finally {
      operationLock.current = false;

      if (mounted.current) {
        setLoadingSuggestions(false);
      }
    }
  };

  const updateAutoAssignment = (
    stripeId: string,
    update: Partial<StripeAssignment>
  ): void => {
    if (busy || reloadRequired || assignments[stripeId]?.legacy === true) {
      return;
    }

    const current = assignments[stripeId];
    const productChanged =
      update.productId !== undefined && update.productId !== current?.productId;

    const nextProduct = availableProducts.find(
      (product) => product.id === update.productId
    );

    setAssignments((previous) => ({
      ...previous,
      [stripeId]: {
        productId: current?.productId ?? "",
        rate: productChanged
          ? update.productId === FIELD_STANDARD_PRODUCT_ID
            ? standardRate
            : defaultProductRate(nextProduct)
          : current?.rate ?? null,
        color: productChanged ? nextProduct?.color ?? "" : current?.color ?? "",
        legacy: false,
        ...update,
      },
    }));

    if (productChanged) {
      if (
        update.productId === FIELD_STANDARD_PRODUCT_ID &&
        current !== undefined
      ) {
        setStandardStripeIds((previous) =>
          Array.from(new Set([...previous, stripeId]))
        );
        setStandardAssignmentSnapshots((previous) => ({
          ...previous,
          [stripeId]: { ...current },
        }));
      } else {
        setStandardStripeIds((previous) =>
          previous.filter((id) => id !== stripeId)
        );
        setStandardAssignmentSnapshots((previous) => {
          const next = { ...previous };
          Reflect.deleteProperty(next, stripeId);
          return next;
        });
      }
    }
  };

  const updateStandardRate = (stripeId: string, checked: boolean): void => {
    const current = assignments[stripeId];

    if (
      current === undefined ||
      current.legacy === true ||
      busy ||
      reloadRequired ||
      (checked && !canUseStandardRate)
    ) {
      return;
    }

    if (checked) {
      setStandardAssignmentSnapshots((previous) => ({
        ...previous,
        [stripeId]: { ...current },
      }));
      setAssignments((previous) => ({
        ...previous,
        [stripeId]: {
          ...current,
          productId: FIELD_STANDARD_PRODUCT_ID,
          rate: standardRate,
          color: selectedStandardVariety?.color ?? current.color,
        },
      }));
    } else {
      const snapshot =
        standardAssignmentSnapshots[stripeId] ?? persisted[stripeId];

      if (snapshot !== undefined) {
        setAssignments((previous) => ({
          ...previous,
          [stripeId]: { ...snapshot },
        }));
      }

      setStandardAssignmentSnapshots((previous) => {
        const next = { ...previous };
        Reflect.deleteProperty(next, stripeId);
        return next;
      });
    }

    setStandardStripeIds((previous) =>
      checked
        ? Array.from(new Set([...previous, stripeId]))
        : previous.filter((id) => id !== stripeId)
    );
  };

  const backFromAutoAssignment = async (): Promise<void> => {
    if (busy || operationLock.current) return;

    if (!assignmentsDirty && !autoDraft && !standardDirty) {
      setStandardStripeIds([]);
      setStandardAssignmentSnapshots({});
      setShowAutoAssignment(false);
      return;
    }

    if (!isComplete) {
      toast.error("Correct the seed assignments before leaving this review.");
      return;
    }

    let trial = api.trial;

    if (trial === undefined) return;

    operationLock.current = true;
    setContinuing(true);

    try {
      if (assignmentsDirty || autoDraft) {
        const explicitlySaved = Array.from(
          new Set(
            Object.values(effectiveAssignments)
              .filter((item) => item.legacy !== true)
              .map((item) => item.productId)
          )
        );
        const payload = buildStripSeedPayload(
          trial,
          stripes,
          persisted,
          effectiveAssignments,
          data.products,
          data.unit,
          explicitlySaved,
          actualStandard,
          { replaceAllAssignments: autoDraft }
        );
        trial = await api.saveAssignments({ trial, products: payload });
      }

      if (standardDirty) {
        if (
          selectedStandardVariety === undefined ||
          standardRate === null ||
          getRateError(standardRate) !== undefined
        ) {
          throw new Error("Select a field standard product and valid rate.");
        }

        trial = await api.saveStandard({
          trial,
          standard: {
            standard_seed_product_id: selectedStandardVariety.id,
            standard_product: selectedStandardVariety.name,
            standard_seed_rate: standardRate,
          },
        });
      }

      const freshTrial = validateStripSeedTrial(
        await fetchSeedTrial(api.trialId, api.scope)
      );
      reconcile(freshTrial);
      setStandardStripeIds([]);
      setStandardAssignmentSnapshots({});
      setAutoDraft(false);
      setShowAutoAssignment(false);
    } catch (error) {
      reportFailure(error);
    } finally {
      operationLock.current = false;
      if (mounted.current) setContinuing(false);
    }
  };

  const saveBeforeBack = async (): Promise<boolean> => {
    if (busy || reloadRequired || operationLock.current) return false;

    if (isManualSelectionActive) {
      if (manualAssignmentDraftDirty) {
        if (!canSave) {
          toast.error("Correct the seed assignments before leaving Seeds.");
          return false;
        }

        if (!(await saveProduct())) return false;
      } else {
        closeManualSelection();
      }
    }

    if (!assignmentsDirty && !autoDraft && !standardDirty) return true;

    let trial = api.trial;
    if (trial === undefined) return false;

    if ((assignmentsDirty || autoDraft) && !isComplete) {
      toast.error("Correct the seed assignments before leaving Seeds.");
      return false;
    }

    operationLock.current = true;
    setContinuing(true);

    try {
      if (assignmentsDirty || autoDraft) {
        const explicitlySaved = Array.from(
          new Set(
            Object.values(effectiveAssignments)
              .filter((item) => item.legacy !== true)
              .map((item) => item.productId)
          )
        );
        const payload = buildStripSeedPayload(
          trial,
          stripes,
          persisted,
          effectiveAssignments,
          data.products,
          data.unit,
          explicitlySaved,
          actualStandard,
          { replaceAllAssignments: autoDraft }
        );
        trial = await api.saveAssignments({ trial, products: payload });
      }

      if (standardDirty) {
        if (
          selectedStandardVariety === undefined ||
          standardRate === null ||
          getRateError(standardRate) !== undefined
        ) {
          throw new Error("Select a field standard product and valid rate.");
        }

        trial = await api.saveStandard({
          trial,
          standard: {
            standard_seed_product_id: selectedStandardVariety.id,
            standard_product: selectedStandardVariety.name,
            standard_seed_rate: standardRate,
          },
        });
      }

      const freshTrial = validateStripSeedTrial(
        await fetchSeedTrial(api.trialId, api.scope)
      );
      reconcile(freshTrial);
      setStandardStripeIds([]);
      setStandardAssignmentSnapshots({});
      setAutoDraft(false);
      setShowAutoAssignment(false);
      return true;
    } catch (error) {
      reportFailure(error);
      return false;
    } finally {
      operationLock.current = false;
      if (mounted.current) setContinuing(false);
    }
  };

  useLayoutEffect(() => {
    onRegisterBeforeBack?.(saveBeforeBack);

    return () => {
      onRegisterBeforeBack?.(null);
    };
  });

  /**
   * Only an explicit CP & Biologicals action saves pending Seeds changes
   * and creates the seed agricultural operation.
   */
  const prepareContinue = async (): Promise<boolean> => {
    let trial = api.trial;

    if (
      !active ||
      trial === undefined ||
      !isComplete ||
      busy ||
      reloadRequired ||
      operationLock.current
    ) {
      return false;
    }

    operationLock.current = true;
    setContinuing(true);

    try {
      if (assignmentsDirty || autoDraft) {
        const explicitlySaved = Array.from(
          new Set(
            Object.values(effectiveAssignments)
              .filter((item) => item.legacy !== true)
              .map((item) => item.productId)
          )
        );

        const payload = buildStripSeedPayload(
          trial,
          stripes,
          persisted,
          effectiveAssignments,
          data.products,
          data.unit,
          explicitlySaved,
          actualStandard,
          { replaceAllAssignments: autoDraft }
        );

        trial = await api.saveAssignments({ trial, products: payload });

        try {
          reconcile(trial);
        } catch {
          throw new StripSeedConfirmationError();
        }
      }

      if (standardDirty) {
        if (
          selectedStandardVariety === undefined ||
          standardRate === null ||
          getRateError(standardRate) !== undefined
        ) {
          throw new Error(
            "Select a field standard product and enter a valid rate."
          );
        }

        trial = await api.saveStandard({
          trial,
          standard: {
            standard_seed_product_id: selectedStandardVariety.id,
            standard_product: selectedStandardVariety.name,
            standard_seed_rate: standardRate,
          },
        });
      }

      if (!mounted.current) return false;

      try {
        const freshTrial = await fetchSeedTrial(api.trialId, api.scope);

        if (!mounted.current) return false;

        await triggerSeedAgOperation(freshTrial, ["seed"], api.scope);
      } catch (error) {
        if (mounted.current) {
          const reason =
            error instanceof Error
              ? error.message
              : "An unexpected error occurred.";

          toast.error(
            "Your saved seed assignments have been retained, but the " +
              "agricultural operation creation request could not be " +
              `confirmed. ${reason}`
          );
        }

        // Do not automatically retry a POST with an uncertain outcome.
        return false;
      }

      return mounted.current;
    } catch (error) {
      reportFailure(error);
      return false;
    } finally {
      operationLock.current = false;

      if (mounted.current) {
        setContinuing(false);
      }
    }
  };

  useLayoutEffect(() => {
    onRegisterBeforeLeave?.(async (): Promise<boolean> => {
      if (!continueRequested.current) {
        if (
          isManualSelectionActive ||
          assignmentsDirty ||
          autoDraft ||
          standardDirty
        ) {
          toast.info("Use CP & Biologicals to save your pending seed changes.");
          return false;
        }

        return !busy && !reloadRequired;
      }

      try {
        return await prepareContinue();
      } finally {
        continueRequested.current = false;
      }
    });

    return () => {
      onRegisterBeforeLeave?.(null);
    };
  });

  const handleContinue = async (): Promise<void> => {
    if (
      !active ||
      !isComplete ||
      busy ||
      reloadRequired ||
      operationLock.current ||
      continueRequested.current
    ) {
      return;
    }

    continueRequested.current = true;

    if (onRegisterBeforeLeave !== undefined) {
      try {
        onContinue();
      } catch (error) {
        continueRequested.current = false;

        if (mounted.current) {
          toast.error(
            error instanceof Error
              ? error.message
              : "Could not continue from the Seeds tab."
          );
        }
      }

      return;
    }

    try {
      if (await prepareContinue()) {
        onContinue();
      }
    } finally {
      continueRequested.current = false;
    }
  };

  const handleBack = (): void => {
    if (busy) return;

    if (isManualSelectionActive) {
      closeManualSelection();
      return;
    }

    onBack();
  };

  const handleStandardProductChange = (productId: string): void => {
    if (busy || reloadRequired) return;

    setStandardProduct(productId);
  };

  if (autoAssignmentFailed) {
    return (
      <ErrorAutoScreen
        isRetrying={loadingSuggestions}
        onRetry={() => {
          void requestAutoAssignment();
        }}
        onClose={() => {
          setAutoAssignmentFailed(false);
        }}
      />
    );
  }

  if (showAutoAssignment) {
    return (
      <StripeAutoAssignment
        unit={displayUnit}
        products={availableProducts}
        standardProductId={FIELD_STANDARD_PRODUCT_ID}
        stripes={stripes}
        assignments={effectiveAssignments}
        standardStripeIds={standardStripeIds}
        canUseStandardRate={canUseStandardRate}
        errors={assignmentErrors}
        validAssignmentCount={validAssignmentCount}
        isComplete={isComplete}
        onAssignmentChange={updateAutoAssignment}
        onStandardRateChange={updateStandardRate}
        onBack={() => {
          void backFromAutoAssignment();
        }}
        nextLabel={nextLabel}
        onContinue={() => {
          void handleContinue();
        }}
      />
    );
  }

  return (
    <div className="rd-stripe-seeds" data-testid="stripe-seeds-tab">
      <StdSeedRate
        productOptions={data.standardVarieties.map((product) => ({
          value: product.id,
          label: product.name,
        }))}
        productValue={standardProduct}
        onProductChange={handleStandardProductChange}
        seedRateValue={standardRate}
        onSeedRateChange={(rate) => {
          if (!busy && !reloadRequired) {
            setStandardRate(rate);
          }
        }}
        seedRateLabel={text("seedRateLabel", { unit: displayUnit })}
        seedRateError={standardError}
      />

      <PlotSummary
        totalPlots={stripes.length}
        assigned={
          isManualSelectionActive ? summarySelectedCount : validAssignmentCount
        }
        totalLabel={text("totalStripes")}
        assignedLabel={text("selected")}
        missingLabel={text("missing")}
        applicationAssigned={
          isManualSelectionActive ? summaryProductCount : usedProductCount
        }
        applicationTotal={availableProducts.length}
        applicationLabel={text("products")}
      />

      <WarningConfirmationPopover
        open={overwriteConfirmationOpen}
        ariaLabel="Overwrite existing assignments"
        message="Proceeding with auto product assignment will overwrite all current entries."
        onCancel={() => {
          setOverwriteConfirmationOpen(false);
        }}
        onConfirm={() => {
          setOverwriteConfirmationOpen(false);
          void requestAutoAssignment();
        }}
      >
        <AutoProdAssignment
          disabled={
            !data.ready ||
            busy ||
            reloadRequired ||
            stripes.length === 0 ||
            selectedStandardVariety === undefined ||
            isManualSelectionActive
          }
          onClick={() => {
            if (
              selectedStandardVariety === undefined ||
              busy ||
              reloadRequired
            ) {
              return;
            }
            if (overwriteConfirmationOpen) {
              setOverwriteConfirmationOpen(false);
              return;
            }

            if (
              Object.values(persisted).some(
                (assignment) => assignment.productId
              )
            ) {
              setOverwriteConfirmationOpen(true);
              return;
            }

            void requestAutoAssignment();
          }}
        />
      </WarningConfirmationPopover>

      {stripes.length === 0 && <p role="status">{text("noStripes")}</p>}

      {availableProducts.map((product) => {
        const isEditing = editingProductId === product.id;
        const isStandardProduct = product.id === FIELD_STANDARD_PRODUCT_ID;
        const productIndex = data.products.findIndex(
          (item) => item.id === product.id
        );
        const productDotColor = isStandardProduct
          ? product.color
          : getSeedProductFamilyColor(Math.max(0, productIndex));

        const assignedCount = stripes.filter(
          ({ id }) => effectiveAssignments[id]?.productId === product.id
        ).length;

        const cardClassName = [
          "rd-stripe-seeds__card",
          assignedCount > 0 && !isEditing
            ? "rd-stripe-seeds__card--assigned"
            : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <section
            key={product.id}
            className={cardClassName}
            aria-label={
              isStandardProduct
                ? text("fieldStandardAria", { product: product.name })
                : product.name
            }
            style={
              assignedCount > 0 ? { borderColor: productDotColor } : undefined
            }
          >
            <div className="rd-stripe-seeds__product-heading">
              <span
                className="rd-stripe-seeds__dot"
                style={{ backgroundColor: productDotColor }}
                aria-hidden
              />

              <strong>{product.name}</strong>

              {isEditing ? (
                <ResetBtn
                  className="rd-stripe-seeds__product-reset"
                  disabled={
                    busy ||
                    reloadRequired ||
                    (selectedIds.length === 0 &&
                      !hasPersistedProductAssignments)
                  }
                  onClick={() => {
                    void resetManualSelection();
                  }}
                />
              ) : assignedCount > 0 && !isEditing ? (
                <span className="rd-stripe-seeds__count">
                  <span aria-hidden>•</span>{" "}
                  {text("stripsSelected", { count: assignedCount })}
                </span>
              ) : null}
            </div>

            <div className="rd-stripe-seeds__range">
              {isStandardProduct
                ? text("standardRateValue", {
                    rate: standardRate ?? "—",
                    unit: data.unit,
                  })
                : rangeLabel(product)}
            </div>

            {isEditing ? (
              <>
                <details className="rd-stripe-seeds__strips" open>
                  <summary>
                    <DownOutlined aria-hidden />
                    {text("strips")}
                  </summary>

                  <StripeManualAssignmentTable
                    unit={displayUnit}
                    rateOptions={product.densityOptions}
                    stripes={stripes}
                    rates={manualDisplayRates}
                    errors={manualRateErrors}
                    colors={stripeColors}
                    rateReadOnly={isEditingStandard}
                    accentColor={product.color}
                    highlightedId={plotSelection.focusedId}
                    onHighlightChange={(id) => {
                      if (busy) return;

                      const focusedId = plotSelection.focusedId;

                      if (id !== null) {
                        if (focusedId !== id) {
                          plotSelection.toggleFocus(id);
                        }
                      } else if (focusedId !== null) {
                        plotSelection.toggleFocus(focusedId);
                      }
                    }}
                    onRateChange={(id, rate) => {
                      if (
                        busy ||
                        reloadRequired ||
                        isEditingStandard ||
                        !Object.prototype.hasOwnProperty.call(draft, id)
                      ) {
                        return;
                      }

                      setDraft((previous) => ({
                        ...previous,
                        [id]: rate,
                      }));
                    }}
                  />
                </details>

                <div className="rd-stripe-seeds__actions">
                  <Button disabled={busy} onClick={closeManualSelection}>
                    {text("cancel")}
                  </Button>

                  <Button
                    type="primary"
                    disabled={!canSave}
                    onClick={() => {
                      void saveProduct();
                    }}
                  >
                    {text("save")}
                  </Button>
                </div>
              </>
            ) : (
              <Button
                block
                disabled={
                  !data.ready ||
                  busy ||
                  reloadRequired ||
                  stripes.length === 0 ||
                  isManualSelectionActive
                }
                onClick={() => {
                  openProduct(product.id);
                }}
              >
                {text(assignedCount > 0 ? "edit" : "selectStrips")}
                {assignedCount > 0 ? (
                  <EditIcon aria-hidden />
                ) : (
                  <ArrowRightOutlined />
                )}
              </Button>
            )}
          </section>
        );
      })}

      <div className="rd-stripe-seeds__footer">
        <Button type="text" disabled={busy} onClick={handleBack}>
          <LeftOutlined aria-hidden />
          {text("back")}
        </Button>

        <Button
          type="primary"
          loading={continuing}
          disabled={!isComplete}
          onClick={() => {
            void handleContinue();
          }}
        >
          {nextLabel ?? text("biologicals")}
          <RightOutlined aria-hidden />
        </Button>
      </div>
    </div>
  );
};

const StripeSeedsTab = (props: StripeSeedsTabProps): JSX.Element => {
  const { trialInfo } = useNewTrialState();
  const { orgId, workspaceId } = useTrialsRouter();

  const editorKey = JSON.stringify([
    orgId ?? "",
    workspaceId ?? "",
    trialInfo?.id ?? "",
  ]);

  return <StripeSeedsTabContent key={editorKey} {...props} />;
};

export default StripeSeedsTab;
